import { useEffect, useRef, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  FixedCostCurrency,
  LocalAiLeadDashboardSections,
  LocalAiLeadGroupAttribute,
} from "@/api/admin-api"

import {
  emptyDashboardSections,
  LOCAL_AI_LEAD_DASHBOARD_SECTIONS,
  updateLocalAiLeadConfig,
} from "@/api/admin-api"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"
import { useAuthGeneration } from "@/hooks/use-auth-generation"

import { useLocalAiLeadConfigQuery } from "./local-ai-lead-config-query"
import { LocalAiLeadCostModelSection } from "./local-ai-lead-cost-model-section"
import { LocalAiLeadGroupPicker } from "./local-ai-lead-group-picker"
import { LocalAiLeadLegacyCostFields } from "./local-ai-lead-legacy-cost-fields"

const SECTION_LABEL_DEFAULTS: Record<keyof LocalAiLeadDashboardSections, string> = {
  requests_per_user: "Requests per user",
  shared_cost_lines: "Shared cost lines",
  user_full_cost: "Full cost on the user table",
  headcount_adoption: "Headcount and adoption",
  adoption_curve: "Adoption curve",
  group_overview: "Group overview",
}

function useUpdateLocalAiLeadConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateLocalAiLeadConfig,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-config"] })
      // The fixed monthly cost feeds the Local AI Lead cost tile, so changing it
      // must void the cached cost breakdown (within this client). The grouping
      // attribute feeds the group overview the same way.
      void queryClient.invalidateQueries({ queryKey: ["localAiLead", "costBreakdown"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-group-overview"] })
    },
  })
}

/**
 * Tenant-wide Local AI Lead settings: the grouping attribute / scope group used
 * to assign users, and the fixed monthly shared costs that feed the Local AI
 * Lead dashboard cost tile.
 *
 * The two halves are gated differently, and have been since the roster became
 * delegable (#3093): the grouping attribute and scope group are part of the
 * `fair_use` capability, while the three cost figures are commercial inputs
 * that `PUT /api/admin/local-ai-lead-config` answers 403
 * `platform_admin_required` for — applying NOTHING, not even the delegable
 * fields in the same payload. `canEditCosts` therefore has to keep them off
 * the wire as well as out of reach; rendering them editable to a delegated
 * admin would turn one Save into a generic failure toast and a dirty form
 * that the unsaved-changes guard will not let them leave.
 */
type LocalAiLeadSettingsSectionProps = {
  canEditCosts: boolean
  section?: "all" | "assignment" | "costs"
}

/**
 * Remounts the whole section on an auth-session change, so neither its drafts
 * nor the annual cost form inside it can carry the previous principal's
 * figures into the new session.
 */
export function LocalAiLeadSettingsSection(props: LocalAiLeadSettingsSectionProps) {
  const authGeneration = useAuthGeneration()
  return <LocalAiLeadSettingsSectionBody key={authGeneration} {...props} />
}

function LocalAiLeadSettingsSectionBody({
  canEditCosts,
  section = "all",
}: LocalAiLeadSettingsSectionProps) {
  const { t } = useTranslation()
  const showAssignment = section !== "costs"
  const showCosts = section !== "assignment"
  const configQuery = useLocalAiLeadConfigQuery()
  const updateConfigMutation = useUpdateLocalAiLeadConfigMutation()

  const [configDraft, setConfigDraft] = useState<LocalAiLeadGroupAttribute | null>(null)
  const [groupDraftId, setGroupDraftId] = useState<string | null>(null)
  const [groupDraftName, setGroupDraftName] = useState<string | null>(null)
  const [groupDraftDirty, setGroupDraftDirty] = useState(false)
  // Single combined fixed monthly cost (kept as a string while editing so the
  // input can be cleared; parsed to a number on save).
  const [platformCostDraft, setPlatformCostDraft] = useState<string | null>(null)
  // Flat per-assigned-user infra-cost assumption (same string-draft treatment).
  const [infraCostDraft, setInfraCostDraft] = useState<string | null>(null)
  const [costCurrencyDraft, setCostCurrencyDraft] = useState<FixedCostCurrency | null>(null)
  const [projectCostDraft, setProjectCostDraft] = useState<string | null>(null)
  const [seatBenchmarkDraft, setSeatBenchmarkDraft] = useState<string | null>(null)
  const [sectionsDraft, setSectionsDraft] = useState<LocalAiLeadDashboardSections | null>(null)

  // Sync drafts from server data
  const currentGroupAttr = configQuery.data?.local_ai_lead_config.group_attribute
  const currentGroupId = configQuery.data?.local_ai_lead_config.user_group_id ?? null
  const currentGroupName = configQuery.data?.local_ai_lead_config.user_group_name ?? null
  const allowedGroupAttrs = configQuery.data?.allowed_group_attributes ?? []
  // Fold any legacy split-out subscription cost into the combined platform cost
  // so older configs still surface the full amount in the single input.
  const currentPlatformCost =
    (configQuery.data?.local_ai_lead_config.monthly_platform_cost ?? 0) +
    (configQuery.data?.local_ai_lead_config.monthly_subscription_cost ?? 0)
  const currentCostCurrency = configQuery.data?.local_ai_lead_config.fixed_cost_currency ?? "USD"
  const currentInfraCost = configQuery.data?.local_ai_lead_config.fixed_infra_cost_per_user ?? 0
  const currentProjectCost = configQuery.data?.local_ai_lead_config.annual_project_cost ?? 0
  const currentSeatBenchmark = configQuery.data?.local_ai_lead_config.seat_benchmark_annual ?? 0
  const costModel = configQuery.data?.local_ai_lead_config.cost_model
  const legacyCostEditable = canEditCosts && costModel !== "shared_annual_v1"
  const currentSections: LocalAiLeadDashboardSections = {
    ...emptyDashboardSections(),
    ...configQuery.data?.local_ai_lead_config.dashboard_sections,
  }

  const attrIsDirty = configDraft !== null && configDraft !== currentGroupAttr
  // A legacy cost field is dirty only while those inputs can still be saved.
  // shared_annual_v1 hides them; a stale draft must not arm Save.
  const platformCostIsDirty =
    legacyCostEditable &&
    platformCostDraft !== null &&
    platformCostDraft !== String(currentPlatformCost)
  const infraCostIsDirty =
    legacyCostEditable && infraCostDraft !== null && infraCostDraft !== String(currentInfraCost)
  const costCurrencyIsDirty =
    legacyCostEditable && costCurrencyDraft !== null && costCurrencyDraft !== currentCostCurrency
  const projectCostIsDirty =
    legacyCostEditable &&
    projectCostDraft !== null &&
    projectCostDraft !== String(currentProjectCost)
  const seatBenchmarkIsDirty =
    legacyCostEditable &&
    seatBenchmarkDraft !== null &&
    seatBenchmarkDraft !== String(currentSeatBenchmark)
  const sectionsAreDirty =
    sectionsDraft !== null &&
    LOCAL_AI_LEAD_DASHBOARD_SECTIONS.some((key) => sectionsDraft[key] !== currentSections[key])
  const configIsDirty =
    (showAssignment && (attrIsDirty || groupDraftDirty)) ||
    (showCosts &&
      (platformCostIsDirty ||
        infraCostIsDirty ||
        costCurrencyIsDirty ||
        projectCostIsDirty ||
        seatBenchmarkIsDirty ||
        sectionsAreDirty))
  useRegisterAdminDirty("local-ai-lead-settings", configIsDirty)

  useEffect(() => {
    if (currentGroupAttr) setConfigDraft(currentGroupAttr)
  }, [currentGroupAttr])

  useEffect(() => {
    setGroupDraftId(currentGroupId)
    setGroupDraftName(currentGroupName)
  }, [currentGroupId, currentGroupName])

  // Adopt server-provided cost values only for fields the user hasn't edited,
  // so a background config refetch (e.g. window focus) can't wipe in-progress
  // edits. A field is "clean" when its draft still equals the last value we
  // synced from the server; otherwise it's dirty and we leave it untouched.
  // shared_annual_v1 hides the five legacy inputs, so drop those drafts.
  const lastSyncedCosts = useRef<{
    platform: string
    infra: string
    currency: FixedCostCurrency
    project: string
    benchmark: string
  } | null>(null)

  useEffect(() => {
    const serverPlatform = String(currentPlatformCost)
    const serverInfra = String(currentInfraCost)
    const serverProject = String(currentProjectCost)
    const serverBenchmark = String(currentSeatBenchmark)
    const dropLegacyDrafts = costModel === "shared_annual_v1"
    const prev = lastSyncedCosts.current
    setPlatformCostDraft((d) =>
      dropLegacyDrafts || prev === null || d === prev.platform ? serverPlatform : d
    )
    setInfraCostDraft((d) =>
      dropLegacyDrafts || prev === null || d === prev.infra ? serverInfra : d
    )
    setProjectCostDraft((d) =>
      dropLegacyDrafts || prev === null || d === prev.project ? serverProject : d
    )
    setSeatBenchmarkDraft((d) =>
      dropLegacyDrafts || prev === null || d === prev.benchmark ? serverBenchmark : d
    )
    setCostCurrencyDraft((d) =>
      dropLegacyDrafts || prev === null || d === prev.currency ? currentCostCurrency : d
    )
    lastSyncedCosts.current = {
      platform: serverPlatform,
      infra: serverInfra,
      currency: currentCostCurrency,
      project: serverProject,
      benchmark: serverBenchmark,
    }
  }, [
    costModel,
    currentPlatformCost,
    currentInfraCost,
    currentCostCurrency,
    currentProjectCost,
    currentSeatBenchmark,
  ])

  const handleSaveConfig = async () => {
    try {
      const updates: Record<string, unknown> = {}
      if (showAssignment && configDraft && configDraft !== currentGroupAttr) {
        updates.group_attribute = configDraft
      }
      if (showAssignment && groupDraftDirty) {
        updates.user_group_id = groupDraftId || null
        updates.user_group_name = groupDraftName || null
      }
      if (showCosts && legacyCostEditable) {
        if (platformCostIsDirty) {
          const parsed = Number(platformCostDraft)
          if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error(
              t(
                "adminDashboard.localAiLeadSettings.invalidCost",
                "Cost must be a non-negative number"
              )
            )
            return
          }
          updates.monthly_platform_cost = parsed
        }
        if (infraCostIsDirty) {
          const parsed = Number(infraCostDraft)
          if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error(
              t(
                "adminDashboard.localAiLeadSettings.invalidCost",
                "Cost must be a non-negative number"
              )
            )
            return
          }
          updates.fixed_infra_cost_per_user = parsed
        }
        if (costCurrencyIsDirty) {
          updates.fixed_cost_currency = costCurrencyDraft
        }
        if (projectCostIsDirty) {
          const parsed = Number(projectCostDraft)
          if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error(
              t(
                "adminDashboard.localAiLeadSettings.invalidCost",
                "Cost must be a non-negative number"
              )
            )
            return
          }
          updates.annual_project_cost = parsed
        }
        if (seatBenchmarkIsDirty) {
          const parsed = Number(seatBenchmarkDraft)
          if (!Number.isFinite(parsed) || parsed < 0) {
            toast.error(
              t(
                "adminDashboard.localAiLeadSettings.invalidCost",
                "Cost must be a non-negative number"
              )
            )
            return
          }
          updates.seat_benchmark_annual = parsed
        }
      }
      if (showCosts && sectionsAreDirty) {
        updates.dashboard_sections = sectionsDraft
      }
      await updateConfigMutation.mutateAsync(updates)
      setGroupDraftDirty(false)
      setSectionsDraft(null)
      toast.success(
        t("adminDashboard.localAiLeadSettings.savedToast", "Local AI Lead settings updated")
      )
    } catch (error) {
      toast.error(
        t(
          "adminDashboard.localAiLeadSettings.saveErrorToast",
          "Failed to update Local AI Lead settings"
        ),
        { description: error instanceof Error ? error.message : String(error) }
      )
    }
  }

  if (configQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner className="size-4" />
      </div>
    )
  }
  if (configQuery.isError) {
    return (
      <p className="text-destructive mb-4 text-sm">
        {t(
          "adminDashboard.localAiLeadSettings.loadErrorToast",
          "Failed to load Local AI Lead settings."
        )}
      </p>
    )
  }

  const legacyCostSetters = {
    platformCost: setPlatformCostDraft,
    infraCost: setInfraCostDraft,
    projectCost: setProjectCostDraft,
    seatBenchmark: setSeatBenchmarkDraft,
  }

  return (
    <div className="mb-6 space-y-4" data-admin-dirty={configIsDirty ? "true" : undefined}>
      {showAssignment ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="local-ai-lead-group-attribute">
              {t("adminDashboard.localAiLeadSettings.groupAttributeLabel", "Grouping attribute")}
            </Label>
            <Select
              value={configDraft ?? currentGroupAttr ?? "department"}
              onValueChange={(v) => setConfigDraft(v as LocalAiLeadGroupAttribute)}
            >
              <SelectTrigger id="local-ai-lead-group-attribute">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedGroupAttrs.map((attr) => (
                  <SelectItem key={attr} value={attr}>
                    {t(`adminDashboard.localAiLeadSettings.attribute.${attr}`, attr)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <LocalAiLeadGroupPicker
            groupId={groupDraftId}
            groupName={groupDraftName}
            onChange={(id, name) => {
              setGroupDraftId(id)
              setGroupDraftName(name)
              setGroupDraftDirty(true)
            }}
          />
        </div>
      ) : null}

      {showCosts ? (
        <>
          {canEditCosts && configQuery.data ? (
            <LocalAiLeadCostModelSection config={configQuery.data.local_ai_lead_config} />
          ) : null}
          {/* ── Cost-estimate fixed figures (feed the Local AI Lead cost tile) ── */}
          {legacyCostEditable ? (
            <LocalAiLeadLegacyCostFields
              currency={costCurrencyDraft ?? currentCostCurrency}
              disabled={!legacyCostEditable}
              infraCost={infraCostDraft}
              platformCost={platformCostDraft}
              projectCost={projectCostDraft}
              seatBenchmark={seatBenchmarkDraft}
              onChange={(field, value) => legacyCostSetters[field](value)}
              onCurrencyChange={setCostCurrencyDraft}
            />
          ) : null}

          {!canEditCosts ? (
            <p
              className="text-muted-foreground text-xs"
              data-testid="local-ai-lead-cost-platform-only"
            >
              {t(
                "adminDashboard.localAiLeadSettings.costPlatformOnly",
                "These figures are set by your platform provider. Everything else here is yours to change."
              )}
            </p>
          ) : null}

          <div className="space-y-3 border-t pt-4">
            <div>
              <p className="text-sm font-medium">
                {t("adminDashboard.localAiLeadSettings.sectionsTitle", "Dashboard sections")}
              </p>
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.localAiLeadSettings.sectionsHelp",
                  "Off by default. Turn a section on to add it for this tenant."
                )}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {LOCAL_AI_LEAD_DASHBOARD_SECTIONS.map((key) => {
                const checked = (sectionsDraft ?? currentSections)[key]
                return (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <Label htmlFor={`local-ai-lead-section-${key}`}>
                      {t(
                        `adminDashboard.localAiLeadSettings.section.${key}`,
                        SECTION_LABEL_DEFAULTS[key]
                      )}
                    </Label>
                    <Switch
                      checked={checked}
                      id={`local-ai-lead-section-${key}`}
                      onCheckedChange={(next) => {
                        setSectionsDraft({
                          ...(sectionsDraft ?? currentSections),
                          [key]: next,
                        })
                      }}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : null}

      <div className="flex justify-end">
        <Button
          disabled={!configIsDirty || updateConfigMutation.isPending}
          onClick={() => void handleSaveConfig()}
        >
          {updateConfigMutation.isPending ? <Spinner className="mr-2 size-4" /> : null}
          {t("common.save", "Save")}
        </Button>
      </div>
    </div>
  )
}
